import { clsx } from "@/utils";
import { Icon, Typography } from "..";

interface BaseProps {
  /**
   * Lowest reachable value. Defaults to 0 so a caller can treat "stepped below one" as removal —
   * the Add Extras modal drops the extra entirely at that point.
   */
  min?: number;
  max?: number;
  /**
   * What is being counted ("Baby Capsule"), used for the buttons' accessible names — "Add another
   * Baby Capsule" reads far better than a pair of unlabelled + and − buttons.
   */
  label: string;
  disabled?: boolean;
  className?: string;
}

interface DisplayOnlyProps extends BaseProps {
  editable?: false;
  value: number;
  onChange: (value: number) => void;
}

interface EditableProps extends BaseProps {
  /** Swap the read-only count for a box the user can type a number into. */
  editable: true;
  /** `null` while the box is empty — the user has cleared it to type a new number. */
  value: number | null;
  onChange: (value: number | null) => void;
  /** Error border, for a value the caller won't accept (typed past `max` or under `min`, or left empty). */
  invalid?: boolean;
  /** The box's id, so a visible `<label htmlFor>` can name it. Without one, it's named by `label`. */
  id?: string;
  "aria-describedby"?: string;
}

export type QuantityStepperProps = DisplayOnlyProps | EditableProps;

/**
 * One unit for stepping a count: − , the value, +.
 *
 * Clamping lives here so callers can't drive it out of range, but what the bounds MEAN is theirs:
 * reaching `min` is just a value, not a removal.
 *
 * Display-only by default, since a small count picked by tapping needs no typing. `editable` makes
 * the middle a digits-only box for counts that can run large (a coach's passengers). A typed number
 * is passed through as-is, even past `max`: the caller decides whether it's valid and says so with
 * `invalid`. Only − and + clamp, and leaving the box raises an empty or under-`min` value to `min`.
 */
export const QuantityStepper = (props: QuantityStepperProps) => {
  const { min = 0, max, label, disabled, className } = props;
  const clamp = (next: number) => Math.max(min, max === undefined ? next : Math.min(next, max));
  // An empty box steps as if it held `min`.
  const current = props.value ?? min;
  const step = (next: number) => props.onChange(clamp(next));

  if (props.editable) {
    const { value, onChange, invalid, id } = props;
    const stepClass = "flex size-10 items-center justify-center rounded-lg disabled:bg-neutral-gray disabled:text-neutral-silver-gray";

    return (
      <div className={clsx("inline-flex items-center gap-1", className)}>
        <button
          type="button"
          className={clsx(stepClass, "bg-neutral-gray text-neutral-dark-gray")}
          disabled={disabled || current <= min}
          aria-label={`Remove one ${label}`}
          onClick={() => step(current - 1)}
        >
          <Icon name="Minus" size="lg" />
        </button>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          aria-label={id ? undefined : label}
          aria-describedby={props["aria-describedby"]}
          aria-invalid={invalid || undefined}
          className={clsx(
            "h-10 w-[46px] rounded-lg border bg-white px-1 text-center text-lg tabular-nums text-neutral-black focus:ring-0 disabled:cursor-not-allowed disabled:bg-neutral-surface-gray disabled:text-neutral-silver-gray",
            invalid ? "border-danger focus:border-danger" : "border-neutral-mid-gray focus:border-primary"
          )}
          disabled={disabled}
          value={value ?? ""}
          onChange={(e) => {
            const text = e.target.value;
            // Digits only — no signs, decimals or letters. Anything else leaves the box as it was.
            if (!/^\d*$/.test(text)) return;
            onChange(text === "" ? null : Number(text));
          }}
          onBlur={() => {
            if (value === null || value < min) onChange(min);
          }}
        />
        <button
          type="button"
          className={clsx(stepClass, "bg-primary text-white")}
          disabled={disabled || (max !== undefined && current >= max)}
          aria-label={`Add another ${label}`}
          onClick={() => step(current + 1)}
        >
          <Icon name="Add" size="lg" />
        </button>
      </div>
    );
  }

  // Surface-gray ends against a white middle, so the count reads as the value and the glyphs as the
  // controls. `overflow-hidden` lets those ends fill their corners inside the rounded border.
  const buttonClass = "bg-neutral-surface-gray px-[11px] py-[11px] text-primary disabled:text-neutral-silver-gray";

  return (
    <div className={clsx("inline-flex items-stretch overflow-hidden rounded-lg border border-neutral-mid-gray", className)}>
      <button
        type="button"
        className={buttonClass}
        disabled={disabled || current <= min}
        aria-label={`Remove one ${label}`}
        onClick={() => step(current - 1)}
      >
        <Icon name="Minus" size="sm" />
      </button>
      <div className="flex min-w-[48px] items-center justify-center border-x border-neutral-mid-gray bg-white px-2">
        <Typography variant="title" className="tabular-nums text-neutral-dark-gray">
          {props.value}
        </Typography>
      </div>
      <button
        type="button"
        className={buttonClass}
        disabled={disabled || (max !== undefined && current >= max)}
        aria-label={`Add another ${label}`}
        onClick={() => step(current + 1)}
      >
        <Icon name="Add" size="sm" />
      </button>
    </div>
  );
};
